import { useEffect, useState } from "react";
import { Plus, Trash2, ShieldCheck } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PageTitle } from "@/components/common";
import { toast } from "sonner";

const ROLES = [
  { k: "admin", l: "Admin" },
  { k: "moderator", l: "Moderatore" },
  { k: "viewer", l: "Visualizzatore" },
];
const roleLabel = (r) => ROLES.find((x) => x.k === r)?.l || r;
const NEW = { email: "", password: "", name: "", role: "viewer" };

export default function Utenti() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(NEW);

  const load = () => api.get("/users").then(({ data }) => setUsers(data)).catch((e) => toast.error(formatError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/users", form); toast.success("Utente creato"); setOpen(false); setForm(NEW); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const changeRole = async (id, role) => {
    try { await api.put(`/users/${id}`, { role }); toast.success("Ruolo aggiornato"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("Utente eliminato"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageTitle title="Utenti" subtitle="Gestione Accessi & Ruoli (RBAC)"
        action={<button data-testid="new-user-btn" onClick={() => setOpen(!open)} className="btn-gold clip-beveled px-6 py-2.5 flex items-center gap-2"><Plus className="w-4 h-4" /> Nuovo Utente</button>} />

      {open && (
        <Panel gold className="p-6 mb-6 boot-in" testid="user-form">
          <div className="grid md:grid-cols-4 gap-4">
            <div><label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">Email</label>
              <input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none" /></div>
            <div><label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">Password</label>
              <input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none" /></div>
            <div><label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">Nome</label>
              <input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none" /></div>
            <div><label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">Ruolo</label>
              <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none">
                {ROLES.map((r) => <option key={r.k} value={r.k}>{r.l}</option>)}</select></div>
          </div>
          <div className="flex gap-3 mt-4">
            <button data-testid="save-user-btn" onClick={create} className="btn-gold clip-beveled px-6 py-2">Crea</button>
            <button onClick={() => setOpen(false)} className="btn-outline-gold clip-beveled px-6 py-2">Annulla</button>
          </div>
        </Panel>
      )}

      <Panel className="overflow-x-auto" testid="users-table">
        <table className="w-full min-w-[560px]">
          <thead><tr className="bg-[#0a0a0a] border-b border-[var(--gold)]/40 font-teko text-lg text-[var(--gold-light)] tracking-wide">
            <th className="text-left px-4 py-3">Utente</th><th className="text-left px-4 py-3">Email</th>
            <th className="text-left px-4 py-3">Ruolo</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-[var(--steel)]/20 hover:bg-[#1a1a1a] font-rajdhani" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 text-white flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[var(--gold)]" />{u.name || "—"}</td>
                <td className="px-4 py-3 text-[var(--silver)]">{u.email}</td>
                <td className="px-4 py-3">
                  <select data-testid={`role-select-${u.id}`} value={u.role} onChange={(e) => changeRole(u.id, e.target.value)} disabled={u.id === user?.id}
                    className="bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-2 py-1.5 text-xs text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none disabled:opacity-50">
                    {ROLES.map((r) => <option key={r.k} value={r.k}>{r.l}</option>)}</select>
                </td>
                <td className="px-4 py-3 text-right">
                  {u.id !== user?.id && <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u.id)} className="text-[var(--destructive)]"><Trash2 className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      <p className="font-rajdhani text-xs text-[var(--silver)] mt-3">I ruoli: <span className="text-[var(--gold)]">Admin</span> gestisce tutto · <span className="text-[var(--gold)]">Moderatore</span> e <span className="text-[var(--gold)]">Visualizzatore</span> hanno accesso limitato.</p>
    </div>
  );
}
